/**
 * Maps the device-chat AI payload onto editable review-grid rows.
 *
 * The payload is treated as untrusted: enum-ish fields are coerced onto the canonical
 * vocabularies, numbers are re-parsed, and required-field validation is recomputed
 * client-side so editing a row updates its flags live (the server re-validates on submit).
 */

import { isSafeUrl } from "@/utils/urlUtils";
import type {
  DeviceChatResult,
  DeviceDuplicateCandidate,
  DeviceRowValidation,
  EcolabelDeviceCreateRequest,
  ExtractedDeviceRow,
} from "../api/ecolabelsTypes";
import {
  EcolabelStatus,
  REGISTRATION_AUTHORITIES,
  RENEWABLE_TYPES,
} from "../api/ecolabelsTypes";

/** Editable device fields, used for user-edit preservation across chat turns. */
export const EDITABLE_ROW_FIELDS = [
  "name",
  "country",
  "address",
  "latitude",
  "longitude",
  "renewable_type",
  "technology_code",
  "plant_ecolabel_id",
  "registration_authority",
  "capacity",
  "cod",
  "relevant_link",
  "public_notes",
] as const;
export type EditableRowField = (typeof EDITABLE_ROW_FIELDS)[number];

export interface UploadRow {
  _uid: string;
  selected: boolean;
  manual: boolean;
  /** Snapshot of the AI-extracted values, so later chat turns never clobber a user edit. */
  original: Partial<Record<EditableRowField, unknown>> | null;
  name: string | null;
  country: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  renewable_type: string | null;
  technology_code: string | null;
  plant_ecolabel_id: string | null;
  registration_authority: string | null;
  capacity: number | null;
  cod: string | null;
  relevant_link: string | null;
  public_notes: string | null;
  validation: DeviceRowValidation;
  duplicates: DeviceDuplicateCandidate[];
  /** Outcome of the last Evident device-register lookup for this row's I-REC code
   * (null: no I-REC identity, or not looked up yet). UI state, never submitted. */
  irec_lookup: IrecLookupStatus | null;
}

/** ``loading`` while the register is queried; the rest mirror the API's answer. */
export type IrecLookupStatus =
  "loading" | "found" | "not_found" | "unavailable";

let uidCounter = 0;
const makeUid = () => `upload-row-${++uidCounter}`;

const EMPTY_VALIDATION: DeviceRowValidation = {
  missing_required: [],
  missing_recommended: [],
  registration_id_quality: "MISSING",
};

/** Convert one AI result into grid rows. Rows with any duplicate candidate or missing
 * required field start unselected, so "submit selected" is clean by default. */
export function mapChatResultToRows(
  result: DeviceChatResult | null
): UploadRow[] {
  if (!result?.devices?.length) return [];
  const rows = result.devices.map(mapExtractedRow);
  // Intra-batch duplicate entries reference rows by index — resolve the name and grid uid
  // now, so the panel can say who the suspected twin is and jump to it.
  for (const row of rows) {
    for (const candidate of row.duplicates) {
      if (candidate.draft_index !== null) {
        const other = rows[candidate.draft_index];
        candidate.name = candidate.name ?? other?.name ?? null;
        candidate.draft_uid = other?._uid;
      }
    }
  }
  return rows;
}

/** A blank row for manual entry. Starts unselected until the required fields are filled. */
export function createManualRow(): UploadRow {
  return {
    _uid: makeUid(),
    selected: false,
    manual: true,
    name: null,
    country: null,
    address: null,
    latitude: null,
    longitude: null,
    renewable_type: null,
    technology_code: null,
    plant_ecolabel_id: null,
    registration_authority: null,
    capacity: null,
    cod: null,
    relevant_link: null,
    public_notes: null,
    validation: { ...EMPTY_VALIDATION },
    duplicates: [],
    original: null,
    irec_lookup: null,
  };
}

/** Identity key for reconciling grid rows with a re-emitted chat draft. */
export function rowFingerprint(row: {
  plant_ecolabel_id: string | null;
  name: string | null;
  country: string | null;
}): string {
  const id = row.plant_ecolabel_id?.trim().toUpperCase();
  if (id) return `id:${id}`;
  return `name:${(row.name ?? "").trim().toLowerCase()}|${(row.country ?? "").trim().toUpperCase()}`;
}

/** The row's AI-extracted field values as a stable string; edits don't change it.
 * Used to pair re-emitted rows with their surviving grid counterparts exactly. */
function originalSnapshotKey(row: UploadRow): string {
  const source = row.original ?? row;
  return JSON.stringify(
    EDITABLE_ROW_FIELDS.map(
      (field) =>
        (source as Partial<Record<EditableRowField, unknown>>)[field] ?? null
    )
  );
}

/** Reconciliation identity of an existing grid row: the ORIGINAL extracted identity, so a
 * user's edit to the name or registration ID doesn't orphan the row on the next turn. */
export function existingRowFingerprint(row: UploadRow): string {
  if (row.original) {
    return rowFingerprint({
      plant_ecolabel_id:
        (row.original.plant_ecolabel_id as string | null) ?? null,
      name: (row.original.name as string | null) ?? null,
      country: (row.original.country as string | null) ?? null,
    });
  }
  return rowFingerprint(row);
}

/**
 * Reconcile a new chat result with the current grid without losing user work:
 * - rows the user edited keep their edited field values (the chat's value applies only to
 *   fields the user never touched);
 * - rows the user deleted from the grid stay deleted even though the chat draft still has
 *   them (``deletedFingerprintCounts`` — per-original-identity counts, consumed from a
 *   local copy so the same map keeps suppressing across every later turn);
 * - rows the chat removed disappear; genuinely new rows appear unselected-if-flagged.
 * Fresh rows are paired with surviving grid rows FIRST — exact original-snapshot matches
 * claim their partner, then the rest pair in order — and deletion suppression applies
 * only to the unmatched leftovers. Matching first means a deletion can never steal a
 * kept twin's fresh counterpart (whatever order the chat re-emits rows in), and
 * identity-based suppression means AI corrections to a deleted row's other fields
 * cannot resurrect it.
 * A matched row keeps its existing ``_uid`` (so expansion/selection state and duplicate
 * jump targets survive) and its ``selected`` flag. Manual rows are skipped entirely —
 * the caller re-adds them, so pass only AI rows or expect manual ones to vanish.
 * Returns a new array; input rows may be reused as the merged entries.
 */
export function mergeChatRows(
  existingRows: UploadRow[],
  newRows: UploadRow[],
  deletedFingerprintCounts: Map<string, number>
): UploadRow[] {
  // Queue per fingerprint: two extracted rows can legitimately share an identity (same
  // name+country, no IDs), so matching is one-to-one in order, not one Map slot.
  const existingByFingerprint = new Map<string, UploadRow[]>();
  for (const row of existingRows) {
    if (row.manual) continue;
    const fingerprint = existingRowFingerprint(row);
    const queue = existingByFingerprint.get(fingerprint) ?? [];
    queue.push(row);
    existingByFingerprint.set(fingerprint, queue);
  }

  // Pair fresh rows with survivors. First pass: exact original-snapshot matches, so
  // identity twins re-emitted in a different order can't cross-match and swap their
  // edits/uids. Second pass: remaining fresh rows pair in order.
  const pairedExisting = new Map<string, UploadRow>();
  const unpairedFreshRows: UploadRow[] = [];
  for (const newRow of newRows) {
    const queue = existingByFingerprint.get(rowFingerprint(newRow)) ?? [];
    const newRowSnapshot = originalSnapshotKey(newRow);
    const exactIndex = queue.findIndex(
      (candidate) => originalSnapshotKey(candidate) === newRowSnapshot
    );
    if (exactIndex !== -1) {
      pairedExisting.set(newRow._uid, queue.splice(exactIndex, 1)[0]);
    } else {
      unpairedFreshRows.push(newRow);
    }
  }
  for (const newRow of unpairedFreshRows) {
    const existing = existingByFingerprint.get(rowFingerprint(newRow))?.shift();
    if (existing) pairedExisting.set(newRow._uid, existing);
  }

  const merged: UploadRow[] = [];
  const uidByFreshUid = new Map<string, string>();
  // Local copy: each merge suppresses at most the number of rows deleted so far, and the
  // caller's counts must survive intact for the next chat turn's re-emitted draft.
  const deletionsRemaining = new Map(deletedFingerprintCounts);
  for (const newRow of newRows) {
    const existing = pairedExisting.get(newRow._uid);
    if (!existing) {
      const fingerprint = rowFingerprint(newRow);
      const deletions = deletionsRemaining.get(fingerprint) ?? 0;
      if (deletions > 0) {
        // An unmatched re-emission of a row the user deleted — keep it deleted.
        deletionsRemaining.set(fingerprint, deletions - 1);
        continue;
      }
      merged.push(newRow);
      uidByFreshUid.set(newRow._uid, newRow._uid);
      continue;
    }
    const combined: UploadRow = {
      ...newRow,
      _uid: existing._uid,
      selected: existing.selected,
    };
    for (const field of EDITABLE_ROW_FIELDS) {
      const userEdited =
        existing.original !== null &&
        existing[field] !== existing.original[field];
      if (userEdited) {
        (combined as Record<EditableRowField, unknown>)[field] =
          existing[field];
        if (combined.original)
          combined.original[field] = existing.original?.[field];
      }
    }
    merged.push(combined);
    // The fresh rows referenced each other by their new uids — remember the mapping so the
    // duplicate jump targets can be rewritten to the surviving uids below.
    uidByFreshUid.set(newRow._uid, existing._uid);
  }
  for (const row of merged) {
    for (const candidate of row.duplicates) {
      if (candidate.draft_uid) {
        candidate.draft_uid =
          uidByFreshUid.get(candidate.draft_uid) ?? candidate.draft_uid;
      }
    }
  }
  return merged;
}

/** Numeric grid/panel inputs can hold numbers OR strings (PrimeVue's InputText models
 * strings even under v-model.number); cleared inputs yield "" or NaN. Normalize all of
 * that to a finite number, or null; coordinates must also be inside their axis range. */
export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.trim().replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export type NumberField = "latitude" | "longitude" | "capacity";

export const NUMBER_FIELD_ERROR_KEYS: Record<NumberField, string> = {
  latitude: "ecolabels.form.errors.latitude_range",
  longitude: "ecolabels.form.errors.longitude_range",
  capacity: "ecolabels.form.errors.capacity_invalid",
};

/** The numeric fields that hold something which is not a usable value — distinct from
 * a blank field, so a save can refuse them instead of storing them as empty. */
export function invalidNumberFields(
  row: Partial<Record<NumberField, unknown>>
): NumberField[] {
  const isFilled = (value: unknown) =>
    value !== null && value !== undefined && String(value).trim() !== "";
  const isValid: Record<NumberField, (value: unknown) => boolean> = {
    latitude: isValidLatitude,
    longitude: isValidLongitude,
    capacity: (value) => toFiniteNumber(value) !== null,
  };
  return (["latitude", "longitude", "capacity"] as const).filter(
    (field) => isFilled(row[field]) && !isValid[field](row[field])
  );
}

export function isValidLatitude(value: unknown): boolean {
  const parsed = toFiniteNumber(value);
  return parsed !== null && Math.abs(parsed) <= 90;
}

export function isValidLongitude(value: unknown): boolean {
  const parsed = toFiniteNumber(value);
  return parsed !== null && Math.abs(parsed) <= 180;
}

/** Required fields recomputed live from the row's current values, so edits clear flags.
 * EKOenergy's rule set: a device must be identifiable — name, country, what it is, and
 * where it is (address or coordinates); a registration ID is only meaningful together
 * with the authority that issued it. Vocabulary fields count only when they map onto the
 * canonical values toCreateRequest submits — a free-text value the backend would null out
 * must not unblock a row. Mirrors assess_device_completeness server-side. */
export function missingRequiredFields(row: UploadRow): string[] {
  const missing: string[] = [];
  if (!row.name?.trim()) missing.push("name");
  if (!row.country?.trim()) missing.push("country");
  if (!asVocabulary(row.renewable_type, RENEWABLE_TYPES)) {
    missing.push("renewable_type");
  }
  const hasCoordinates =
    isValidLatitude(row.latitude) && isValidLongitude(row.longitude);
  if (!row.address?.trim() && !hasCoordinates) {
    missing.push("address_or_coordinates");
  }
  if (
    row.plant_ecolabel_id?.trim() &&
    !asVocabulary(row.registration_authority, REGISTRATION_AUTHORITIES)
  ) {
    missing.push("registration_authority");
  }
  // An I-REC-identified row still needs its name and country (the register lookup fills
  // them; a seller whose code is unknown to the register types them) — only technology,
  // location and the authority pairing are waived: the Evident entry stands in for them.
  if (hasIrecIdentity(row)) {
    return missing.filter((field) => !IREC_WAIVED_FIELDS.has(field));
  }
  return missing;
}

/** Mirrors the backend's has_irec_identity: a device identified by its I-REC
 * registration (Evident link, or device code plus the IREC registry) needs no
 * technology or location here — reviewers verify those on Evident (EKOenergy decision). */
export function hasIrecIdentity(row: IrecIdentityFields): boolean {
  return irecDeviceCode(row) !== null;
}

export interface IrecIdentityFields {
  relevant_link?: string | null;
  registration_authority?: string | null;
  plant_ecolabel_id?: string | null;
}

/**
 * The I-REC device code a row is identified by, or null when it is not an I-REC device.
 * Mirrors the backend's irec_device_code: a genuine Evident device-register link identifies
 * the device on its own; a bare registration ID only together with the IREC registry (or
 * such a link) — real codes (PARQWIND004, 52.8ES20001) carry no marker of their own.
 */
export function irecDeviceCode(row: IrecIdentityFields): string | null {
  // An explicit non-IREC authority means this is NOT an I-REC submission, whatever
  // the link says.
  const authority = row.registration_authority?.trim().toUpperCase() ?? "";
  if (authority && authority !== "IREC") return null;
  const linkCode = evidentDeviceCode(row.relevant_link ?? null);
  const explicitCode = row.plant_ecolabel_id?.trim() ?? "";
  const explicitIsCode = IREC_DEVICE_CODE_RE.test(explicitCode);
  // Two different codes (ID says A, link says B) is a contradiction the seller must resolve,
  // not a device to look up — either could be the wrong one.
  if (
    linkCode &&
    explicitIsCode &&
    explicitCode.toUpperCase() !== linkCode.toUpperCase()
  ) {
    return null;
  }
  // A polluted ID cell (several IDs, prose) next to a genuine link: the link identifies the device.
  if (linkCode) return linkCode;
  return explicitIsCode && authority === "IREC" ? explicitCode : null;
}

// Evident Group runs the I-REC device register on these apex domains: registry exports link
// to evident.app; a handful of legacy rows point at evident.global / evident.services.
const EVIDENT_HOSTS = ["evident.app", "evident.global", "evident.services"];
const EVIDENT_DEVICE_PAGE_URL = "https://evident.app/IREC/device-register/";
// The device code is the path segment after ".../device-register/".
const EVIDENT_DEVICE_REGISTER_RE = /\/device-register\/([^/?#]+)\/?$/;
const EVIDENT_RESERVED_SEGMENTS = new Set(["table"]);
// One registry-issued token: letters (Unicode — codes carry accented letters), digits,
// ".", "-", "_". Anything else (spaces, commas, slashes) is prose or several IDs in one cell.
const IREC_DEVICE_CODE_RE = /^[\p{L}\p{N}_.-]{1,64}$/u;
const IREC_WAIVED_FIELDS = new Set([
  "renewable_type",
  "address_or_coordinates",
  "registration_authority",
]);

/** The device code in a genuine Evident device-register link, else null.
 * SECURITY: only the real Evident registry hosts may waive required fields — a
 * lookalike URL (https://attacker.example/evident.app/...) must not. */
export function evidentDeviceCode(link: string | null): string | null {
  let url: URL;
  try {
    url = new URL(link ?? "");
  } catch {
    return null;
  }
  // Same URL policy as the request models: http/https only (no ftp://evident.app).
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  const host = url.hostname.toLowerCase();
  if (
    !EVIDENT_HOSTS.some((apex) => host === apex || host.endsWith(`.${apex}`))
  ) {
    return null;
  }
  const match = EVIDENT_DEVICE_REGISTER_RE.exec(url.pathname);
  if (!match) return null;
  // Exports percent-encode accented codes (E%C3%B3LWIND002); the register keys them decoded.
  let code: string;
  try {
    code = decodeURIComponent(match[1]).trim();
  } catch {
    return null;
  }
  // The register's own pages live under the same prefix (/device-register/table).
  if (EVIDENT_RESERVED_SEGMENTS.has(code.toLowerCase())) return null;
  return IREC_DEVICE_CODE_RE.test(code) ? code : null;
}

/**
 * Fill the identity fields an Evident link / I-REC code determines — the registration ID,
 * the IREC registry and the register link — in the form, so the seller sees exactly what
 * will be stored. Only blank fields are filled; user-entered values are never overwritten.
 * Everything else (name, country, technology, location…) comes from the public register
 * asynchronously (see irecLookup.ts). Returns whether the row carries an I-REC identity.
 */
export function applyIrecIdentity(row: IrecIdentityFields): boolean {
  const code = irecDeviceCode(row);
  if (code === null) return false;
  if (!row.plant_ecolabel_id?.trim()) row.plant_ecolabel_id = code;
  if (!row.registration_authority?.trim()) row.registration_authority = "IREC";
  if (!row.relevant_link?.trim()) {
    row.relevant_link = EVIDENT_DEVICE_PAGE_URL + encodeURIComponent(code);
  }
  return true;
}

/** The device fields the register lookup can fill (a subset of the editable row). */
export interface IrecLookupDevice {
  code: string;
  name: string | null;
  country: string | null;
  address: string | null;
  latitude: number | string | null;
  longitude: number | string | null;
  renewable_type: string | null;
  technology_code: string | null;
  capacity: number | string | null;
  cod: string | null;
  relevant_link: string;
  registration_authority: string;
}

/** A row the register lookup may fill. The numeric trio also admits strings because
 * PrimeVue's InputText models them as such in the field panel (see DeviceFieldsPanel). */
export interface IrecLookupTarget extends IrecIdentityFields {
  name?: string | null;
  country?: string | null;
  address?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  renewable_type?: string | null;
  technology_code?: string | null;
  capacity?: number | string | null;
  /** Present on the add-device form; the register reports MW, so a filled capacity
   * must carry that unit whatever the form had selected. */
  capacity_units?: string;
  cod?: string | null;
}

/**
 * Copy the register's values into the BLANK fields of a row — the seller's own entries
 * always win, and everything lands in the form where it can be corrected before submit.
 * Returns the fields that were filled.
 */
export function applyEvidentLookup(
  row: IrecLookupTarget,
  device: IrecLookupDevice
): string[] {
  const filled: string[] = [];
  const fillText = (
    field:
      | "name"
      | "country"
      | "address"
      | "technology_code"
      | "cod"
      | "renewable_type",
    value: string | null
  ) => {
    if (value === null || value.trim() === "" || row[field]?.trim()) return;
    row[field] = value;
    filled.push(field);
  };
  const fillNumber = (
    field: "latitude" | "longitude" | "capacity",
    value: number | string | null
  ): boolean => {
    // toFiniteNumber rejects blanks and whitespace (Number("  ") would be 0).
    const parsed = toFiniteNumber(value);
    if (parsed === null) return false;
    if (!isBlankNumber(row[field])) return false;
    row[field] = parsed;
    filled.push(field);
    return true;
  };
  fillText("name", device.name);
  fillText("country", device.country?.toUpperCase() ?? null);
  fillText("address", device.address);
  fillText("renewable_type", device.renewable_type?.toUpperCase() ?? null);
  fillText("technology_code", device.technology_code);
  fillText("cod", device.cod);
  // Coordinates only as a valid pair — a lone or out-of-range value would be a half-location
  // that toCreateRequest drops again.
  if (
    isBlankNumber(row.latitude) &&
    isBlankNumber(row.longitude) &&
    isValidLatitude(toFiniteNumber(device.latitude)) &&
    isValidLongitude(toFiniteNumber(device.longitude))
  ) {
    fillNumber("latitude", device.latitude);
    fillNumber("longitude", device.longitude);
  }
  const filledCapacity = fillNumber("capacity", device.capacity);
  if (filledCapacity && "capacity_units" in row) row.capacity_units = "MW";
  return filled;
}

/** A cleared number input reads as null, undefined, "", whitespace or NaN depending on
 * the widget — anything that is not a finite number is blank. Zero is a value. */
function isBlankNumber(value: number | string | null | undefined): boolean {
  return toFiniteNumber(value) === null;
}

export function rowHasBlockingIssues(row: UploadRow): boolean {
  return (
    missingRequiredFields(row).length > 0 || invalidNumberFields(row).length > 0
  );
}

/** Every selectable (non-blocked) row is selected. */
export function areAllSelectableRowsSelected(rows: UploadRow[]): boolean {
  const selectable = rows.filter((row) => !rowHasBlockingIssues(row));
  return selectable.length > 0 && selectable.every((row) => row.selected);
}

/** Select or deselect every selectable row, duplicate-flagged ones included. */
export function applySelectAll(rows: UploadRow[], selected: boolean): void {
  for (const row of rows) {
    if (rowHasBlockingIssues(row)) continue;
    row.selected = selected;
  }
}

/** The conservative variant: the selection becomes EXACTLY the selectable rows without
 * duplicate candidates — blocked and duplicate-flagged rows end up deselected, so a
 * previously ticked row can't sneak back into the payload after being repaired. */
export function applySelectionWithoutDuplicates(rows: UploadRow[]): void {
  for (const row of rows) {
    row.selected = !rowHasBlockingIssues(row) && row.duplicates.length === 0;
  }
}

/** The next row still needing attention for the guided one-by-one review: first
 * unchecked row after `fromUid` (wrapping around), or null when everything is checked. */
export function nextUncheckedRow(
  rows: UploadRow[],
  fromUid?: string
): UploadRow | null {
  const startIndex = fromUid
    ? rows.findIndex((row) => row._uid === fromUid) + 1
    : 0;
  const ordered = [...rows.slice(startIndex), ...rows.slice(0, startIndex)];
  return ordered.find((row) => !row.selected && row._uid !== fromUid) ?? null;
}

export function rowHasWarnings(row: UploadRow): boolean {
  return (
    row.duplicates.length > 0 ||
    row.validation.registration_id_quality === "SUSPECT" ||
    row.validation.registration_id_quality === "MULTIPLE"
  );
}

/** The payload for POST /devices/submit; call only for rows without blocking issues. */
export function toCreateRequest(row: UploadRow): EcolabelDeviceCreateRequest {
  const hasCoordinates =
    isValidLatitude(row.latitude) && isValidLongitude(row.longitude);
  return {
    name: row.name ?? "",
    country: (row.country ?? "").toUpperCase(),
    address: row.address || null,
    // A row can be selectable via its address while carrying junk ("", NaN,
    // out-of-range) in a coordinate input — never submit such values, and only
    // submit coordinates as a complete pair (half a location is no location),
    // normalized to numbers — the inputs can hold numeric strings.
    latitude: hasCoordinates ? toFiniteNumber(row.latitude) : null,
    longitude: hasCoordinates ? toFiniteNumber(row.longitude) : null,
    renewable_type: asVocabulary(row.renewable_type, RENEWABLE_TYPES),
    technology_code: row.technology_code || null,
    plant_ecolabel_id: row.plant_ecolabel_id || null,
    registration_authority: asVocabulary(
      row.registration_authority,
      REGISTRATION_AUTHORITIES
    ),
    capacity: toFiniteNumber(row.capacity),
    capacity_units: "MW",
    cod: isIsoDateString(row.cod) ? row.cod : null,
    relevant_link: safeUrl(row.relevant_link),
    public_notes: row.public_notes || null,
    status: EcolabelStatus.PENDING_REVIEW,
  };
}

function mapExtractedRow(extracted: ExtractedDeviceRow): UploadRow {
  const validation = extracted.validation ?? EMPTY_VALIDATION;
  const duplicates = Array.isArray(extracted.duplicates)
    ? extracted.duplicates
    : [];
  const row: UploadRow = {
    _uid: makeUid(),
    selected: false,
    manual: false,
    name: asString(extracted.name),
    country: asString(extracted.country)?.toUpperCase() ?? null,
    address: asString(extracted.address),
    latitude: asNumber(extracted.latitude),
    longitude: asNumber(extracted.longitude),
    renewable_type: asString(extracted.renewable_type)?.toUpperCase() ?? null,
    technology_code: asString(extracted.technology_code),
    plant_ecolabel_id: asString(extracted.plant_ecolabel_id),
    registration_authority:
      asString(extracted.registration_authority)?.toUpperCase() ?? null,
    capacity: asNumber(extracted.capacity_value),
    cod: asString(extracted.cod),
    relevant_link: safeUrl(asString(extracted.relevant_link)),
    public_notes: asString(extracted.public_notes),
    validation,
    duplicates,
    original: null,
    irec_lookup: null,
  };
  // Derive BEFORE snapshotting: a link-only extraction then shows its code and registry
  // right away, re-emitted drafts fingerprint identically turn after turn, and the
  // derived values count as extraction (an AI correction can still override them), not
  // as seller edits. The register lookup that fills the rest runs asynchronously in the
  // grid (enrichIrecRow) — its values do count as edits, so later turns keep them.
  applyIrecIdentity(row);
  row.original = Object.fromEntries(
    EDITABLE_ROW_FIELDS.map((field) => [field, row[field]])
  );
  row.selected =
    missingRequiredFields(row).length === 0 && duplicates.length === 0;
  return row;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function asNumber(value: unknown): number | null {
  // Number("") and Number("   ") are 0 — a blank cell is missing data, not zero.
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : null;
}

function asVocabulary(
  value: string | null,
  vocabulary: readonly string[]
): string | null {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  return vocabulary.includes(upper) ? upper : null;
}

/** Whether a non-empty link value would survive submission (http/https and well-formed);
 * used to flag values toCreateRequest would otherwise silently null out. */
export function isValidHttpUrl(value: string | null): boolean {
  return safeUrl(value) !== null;
}

/** Strict YYYY-MM-DD, the only shape the API's date field accepts — an AI-extracted
 * malformed date must be dropped rather than abort the whole batch on submit. */
export function isIsoDateString(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  // JS engines overflow impossible days (2026-02-29 → March 1) instead of failing the
  // parse; round-trip so an overflowed value is rejected, not silently shifted.
  return parsed.toISOString().slice(0, 10) === value;
}

// SECURITY: the link is rendered as an `href`; see `isSafeUrl`.
function safeUrl(value: string | null): string | null {
  return value && isSafeUrl(value) ? value : null;
}
